import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, GraduationCap, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/common/States";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { PlanCards } from "@/components/billing/PlanCards";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export function PlansPage() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-background">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-2" data-testid="plans-brand-link">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"><GraduationCap className="h-5 w-5" /></div>
          <span className="font-heading text-lg font-bold">ProfessorHub</span>
        </Link>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button asChild variant="outline" data-testid="plans-nav-btn"><Link to={user ? "/dashboard" : "/login"}>{user ? "Ir para o painel" : "Entrar"}</Link></Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 pb-20 pt-10">
        <h1 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">Planos para cada fase<br /><span className="text-primary">da sua carreira.</span></h1>
        <p className="mt-4 max-w-xl text-base text-muted-foreground">Comece grátis e faça upgrade quando sua turma crescer. Cancele quando quiser.</p>
        <div className="mt-12"><PlanCards currentPlanId={user?.plan?.id} /></div>
      </main>
    </div>
  );
}

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");

  useEffect(() => {
    const sessionId = params.get("session_id");
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); refresh(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { setState("failed"); return; }
      if (++tries < 10) timer = setTimeout(poll, 2000);
      else setState("timeout");
    };
    if (sessionId) poll(); else setState("failed");
    return () => clearTimeout(timer);
  }, [params, refresh]);

  const content = {
    checking: [<Spinner key="s" className="mx-auto h-8 w-8" />, "Confirmando seu pagamento..."],
    paid: [<CheckCircle2 key="c" className="mx-auto h-12 w-12 text-accent" />, "Assinatura ativada com sucesso!"],
    failed: [<XCircle key="x" className="mx-auto h-12 w-12 text-destructive" />, "Não foi possível confirmar o pagamento."],
    timeout: [<Spinner key="t" className="mx-auto h-8 w-8" />, "O pagamento está sendo processado. Verifique sua assinatura em instantes."],
  }[state];

  return (
    <div className="mx-auto max-w-md rounded-2xl border bg-card p-8 text-center" data-testid={`payment-result-${state}`}>
      {content[0]}<p className="mt-4 font-heading text-lg font-semibold">{content[1]}</p>
      <Button asChild className="mt-6" data-testid="payment-result-continue"><Link to="/configuracoes?tab=assinatura">Ver assinatura</Link></Button>
    </div>
  );
}

export function PaymentCancelPage() {
  return (
    <div className="mx-auto max-w-md rounded-2xl border bg-card p-8 text-center" data-testid="payment-canceled">
      <XCircle className="mx-auto h-12 w-12 text-muted-foreground" />
      <p className="mt-4 font-heading text-lg font-semibold">Pagamento cancelado</p>
      <p className="mt-1 text-sm text-muted-foreground">Nenhuma cobrança foi realizada.</p>
      <Button asChild className="mt-6" data-testid="payment-cancel-back"><Link to="/configuracoes?tab=assinatura">Voltar aos planos</Link></Button>
    </div>
  );
}
